/**
 * Thống kê phiên học — Prompt.md §22, §26.
 *
 * Toàn bộ số liệu được tính **tăng dần** (O(1) mỗi frame, bộ nhớ hằng số). Một phiên 60 phút ở
 * 12 FPS là ~43.000 frame: giữ mảng snapshot rồi tính cuối phiên sẽ ăn hàng chục MB RAM trên
 * điện thoại và làm app giật đúng lúc người dùng đang chờ Report. Counter cho kết quả **giống
 * hệt** với chi phí không đổi.
 */

import type { SessionConfig } from "@studystraight/shared-config";
import type {
  PostureEvent,
  PostureIssueType,
  ScoreSample,
  SessionIssueCounters,
} from "@studystraight/shared-types";

const COUNTER_KEY: Record<PostureIssueType, keyof SessionIssueCounters> = {
  HEAD_DOWN: "headDown",
  LEANING_FORWARD: "leaningForward",
  TOO_CLOSE: "tooClose",
  SHOULDER_TILT: "shoulderTilt",
};

/** Ngưỡng "tư thế tốt" cho `goodPosturePercentage` (§26) — cùng mốc với `scoreBands.good`. */
const GOOD_SCORE = 75;

export interface AnalyticsSummary {
  averagePostureScore: number;
  goodPosturePercentage: number;
  issues: SessionIssueCounters;
  scoreSamples: readonly ScoreSample[];
  frameCount: number;
}

export class SessionAnalytics {
  private frames = 0;
  private scoreSum = 0;
  private goodFrames = 0;
  private readonly counters: SessionIssueCounters = {
    headDown: 0,
    leaningForward: 0,
    tooClose: 0,
    shoulderTilt: 0,
  };

  /** Đang gom sample cho khoảng thời gian hiện tại. */
  private bucketStart = 0;
  private bucketSum = 0;
  private bucketFrames = 0;
  private readonly samples: ScoreSample[] = [];

  constructor(private readonly config: SessionConfig) {}

  /**
   * Nạp một frame. `elapsedMs` là thời gian **đã trừ pause** kể từ đầu phiên, không phải
   * timestamp tuyệt đối: nếu dùng timestamp, một phiên tạm dừng 10 phút sẽ tạo ra 20 sample rỗng
   * và đường điểm trong Report có một khoảng phẳng không hề tồn tại.
   */
  pushScore(score: number, elapsedMs: number): void {
    if (!Number.isFinite(score)) return;

    this.frames += 1;
    this.scoreSum += score;
    if (score >= GOOD_SCORE) this.goodFrames += 1;

    const { scoreSampleIntervalMs } = this.config;
    while (elapsedMs - this.bucketStart >= scoreSampleIntervalMs) {
      this.flushBucket();
      this.bucketStart += scoreSampleIntervalMs;
    }
    this.bucketSum += score;
    this.bucketFrames += 1;
  }

  /**
   * Đếm lần xảy ra của từng issue.
   *
   * Đếm **event**, không đếm frame: state machine chỉ phát event mỗi `alertAfterMs` cho một chuỗi
   * lỗi liên tục, nên "cúi đầu 12 giây" tính là 2 lần thay vì 144 lần như đếm theo frame.
   */
  pushEvents(events: readonly PostureEvent[]): void {
    for (const event of events) this.counters[COUNTER_KEY[event.type]] += 1;
  }

  summarize(): AnalyticsSummary {
    return {
      averagePostureScore: this.frames === 0 ? 0 : Math.round(this.scoreSum / this.frames),
      goodPosturePercentage:
        this.frames === 0 ? 0 : Math.round((this.goodFrames / this.frames) * 100),
      issues: { ...this.counters },
      // Sample dở dang cũng được tính: phiên 40 giây mà Report không có điểm nào thì đường điểm
      // trống trơn, trong khi dữ liệu thì có.
      scoreSamples:
        this.bucketFrames > 0 ? [...this.samples, this.currentSample()] : [...this.samples],
      frameCount: this.frames,
    };
  }

  private flushBucket(): void {
    if (this.bucketFrames === 0) return;
    this.samples.push(this.currentSample());
    this.bucketSum = 0;
    this.bucketFrames = 0;
  }

  private currentSample(): ScoreSample {
    return { atMs: this.bucketStart, score: Math.round(this.bucketSum / this.bucketFrames) };
  }
}
